import { useQuery } from '@tanstack/react-query';
import { intake } from '@lawbee/domain';
import { Badge, Button, Card, CardContent, Dialog, DialogContent, EmptyState, ErrorState, Field, FormError, Input, LoadingState, Select, cn } from '@lawbee/ui';
import { ArrowDown, ArrowUp, FolderOpen, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useApiMutation } from '../../lib/hooks';
import { can, useMe } from '../../lib/me';
import type { MatterType, Stage } from '../../lib/types';
import { ReadOnlyNote, slugKey, Switch } from '../app/ops/shared';

const TYPES_KEY = ['matter-types'] as const;

const AREA_OPTIONS = intake.PRACTICE_AREAS.map((a) => ({ value: a, label: intake.PRACTICE_AREA_LABELS[a] }));

function areaLabel(area: string): string {
  return (intake.PRACTICE_AREA_LABELS as Record<string, string>)[area] ?? area;
}

const DEFAULT_STAGES: Stage[] = [
  { key: 'opened', label: 'Opened', notifyClient: true },
  { key: 'in_progress', label: 'In progress', notifyClient: true },
  { key: 'closed', label: 'Closed', notifyClient: true },
];

export function MatterTypesSettingsPage() {
  const me = useMe();
  const canEdit = can(me, 'settings');
  const q = useQuery({ queryKey: TYPES_KEY, queryFn: () => api.get<{ types: MatterType[] }>('/api/matters/types') });
  const [newOpen, setNewOpen] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-[19px] font-semibold tracking-tight">Matter types</h2>
          <p className="text-sm text-muted-foreground">Each type has its own stages. Stages marked to notify the client trigger a case update when a matter moves into them.</p>
        </div>
        {canEdit ? (
          <Button onClick={() => setNewOpen(true)} className="shrink-0">
            <Plus /> New matter type
          </Button>
        ) : null}
      </div>
      {!canEdit ? <ReadOnlyNote /> : null}
      {q.isLoading ? <LoadingState label="Loading matter types" /> : null}
      {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data && q.data.types.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FolderOpen />}
            title="No matter types yet"
            description="Create one, or choose practice areas during setup to get ready-made types."
            action={canEdit ? <Button onClick={() => setNewOpen(true)}>New matter type</Button> : undefined}
          />
        </Card>
      ) : null}
      <div className="space-y-4">
        {q.data?.types.map((t) => (
          <MatterTypeCard key={t.id} type={t} canEdit={canEdit} />
        ))}
      </div>
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        {newOpen ? <NewTypeForm onClose={() => setNewOpen(false)} /> : null}
      </Dialog>
    </div>
  );
}

type DraftStage = Stage & { isNew?: boolean; keyTouched?: boolean };

function MatterTypeCard({ type, canEdit }: { type: MatterType; canEdit: boolean }) {
  const [name, setName] = useState(type.name);
  const [stages, setStages] = useState<DraftStage[]>(type.stages);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(type.name);
    setStages(type.stages);
  }, [type]);

  const dirty = name !== type.name || JSON.stringify(stages.map(({ isNew: _n, keyTouched: _k, ...s }) => s)) !== JSON.stringify(type.stages);

  const save = useApiMutation((body: { name?: string; stages?: Stage[]; active?: boolean }) => api.patch(`/api/matters/types/${type.id}`, body), {
    success: 'Matter type saved',
    invalidate: [TYPES_KEY],
  });

  const update = (i: number, patch: Partial<DraftStage>) => setStages((s) => s.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i: number, dir: -1 | 1) =>
    setStages((s) => {
      const j = i + dir;
      if (j < 0 || j >= s.length) return s;
      const copy = [...s];
      const a = copy[i];
      const b = copy[j];
      if (!a || !b) return s;
      copy[i] = b;
      copy[j] = a;
      return copy;
    });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 2) return setError('Name must be at least 2 characters.');
    if (stages.length === 0) return setError('A matter type needs at least one stage.');
    const keys = new Set<string>();
    for (const s of stages) {
      if (!s.label.trim()) return setError('Every stage needs a label.');
      if (!/^[a-z][a-z0-9_]{0,39}$/.test(s.key)) return setError(`Stage key "${s.key}" must be lowercase letters, digits and underscores.`);
      if (keys.has(s.key)) return setError(`Two stages share the key "${s.key}".`);
      keys.add(s.key);
    }
    setError(null);
    const clean: Stage[] = stages.map((s) => ({ key: s.key, label: s.label.trim(), notifyClient: s.notifyClient, ...(s.templateKey ? { templateKey: s.templateKey } : {}) }));
    save.mutate({ name: name.trim(), stages: clean }, { onError: (err) => setError(errorMessage(err)) });
  };

  return (
    <Card className={cn(!type.active && 'opacity-80')}>
      <form onSubmit={submit} noValidate>
        <CardContent className="space-y-5 p-5 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1 space-y-2">
              <Field label="Name" htmlFor={`mt-name-${type.id}`}>
                <Input id={`mt-name-${type.id}`} value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} className="sm:max-w-sm" />
              </Field>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{areaLabel(type.practiceArea)}</Badge>
                {!type.active ? <Badge>Inactive</Badge> : null}
              </div>
            </div>
            <Switch
              checked={type.active}
              disabled={!canEdit || save.isPending}
              onCheckedChange={(active) => save.mutate({ active })}
              label="Active"
              description="Inactive types are hidden when opening new matters."
              className="sm:w-64"
            />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold">Stages</h3>
            <ol className="divide-y divide-border/70 rounded-xl border border-border/70">
              {stages.map((s, i) => (
                <li key={i} className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center">
                  <span className="figures w-6 shrink-0 text-xs text-muted-foreground" aria-hidden>
                    {i + 1}
                  </span>
                  <div className="grid flex-1 gap-2 sm:grid-cols-[1fr_12rem]">
                    <Input
                      aria-label={`Stage ${i + 1} label`}
                      value={s.label}
                      disabled={!canEdit}
                      onChange={(e) => update(i, { label: e.target.value, ...(s.isNew && !s.keyTouched ? { key: slugKey(e.target.value) } : {}) })}
                    />
                    <Input
                      aria-label={`Stage ${i + 1} key`}
                      className="figures text-[13px]"
                      value={s.key}
                      disabled={!canEdit || !s.isNew}
                      title={s.isNew ? 'Key used internally' : 'Keys of saved stages cannot change'}
                      onChange={(e) => update(i, { key: e.target.value, keyTouched: true })}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2 sm:justify-end">
                    <label className="flex items-center gap-2 text-[13px]">
                      <Switch checked={s.notifyClient} disabled={!canEdit} onCheckedChange={(v) => update(i, { notifyClient: v })} id={`mt-${type.id}-notify-${i}`} />
                      <span>Notify client</span>
                    </label>
                    {canEdit ? (
                      <div className="flex">
                        <Button type="button" variant="ghost" size="icon" className="size-8" aria-label={`Move stage ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                          <ArrowUp />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="size-8" aria-label={`Move stage ${i + 1} down`} disabled={i === stages.length - 1} onClick={() => move(i, 1)}>
                          <ArrowDown />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          aria-label={`Remove stage ${i + 1}`}
                          disabled={stages.length <= 1}
                          onClick={() => setStages((st) => st.filter((_, j) => j !== i))}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
            {canEdit ? (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="mt-2"
                disabled={stages.length >= 20}
                onClick={() => setStages((s) => [...s, { key: '', label: '', notifyClient: false, isNew: true }])}
              >
                <Plus /> Add stage
              </Button>
            ) : null}
          </div>
          <FormError message={error} />
          {canEdit ? (
            <div className="flex justify-end gap-2">
              {dirty ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setName(type.name);
                    setStages(type.stages);
                    setError(null);
                  }}
                >
                  Discard
                </Button>
              ) : null}
              <Button type="submit" disabled={!dirty} loading={save.isPending}>
                Save changes
              </Button>
            </div>
          ) : null}
        </CardContent>
      </form>
    </Card>
  );
}

function NewTypeForm({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [practiceArea, setPracticeArea] = useState<string>(intake.PRACTICE_AREAS[0]);
  const [error, setError] = useState<string | null>(null);
  const create = useApiMutation((body: { name: string; practiceArea: string; stages: Stage[] }) => api.post('/api/matters/types', body), {
    success: 'Matter type created',
    invalidate: [TYPES_KEY],
    onSuccess: onClose,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 2) return setError('Name must be at least 2 characters.');
    setError(null);
    create.mutate({ name: name.trim(), practiceArea, stages: DEFAULT_STAGES }, { onError: (err) => setError(errorMessage(err)) });
  };
  return (
    <DialogContent
      title="New matter type"
      description="It starts with three stages (Opened, In progress, Closed) that you can rename and extend."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="new-matter-type" loading={create.isPending}>
            Create
          </Button>
        </>
      }
    >
      <form id="new-matter-type" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Name" htmlFor="nmt-name" required>
          <Input id="nmt-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Uncontested divorce" />
        </Field>
        <Field label="Practice area" htmlFor="nmt-area">
          <Select id="nmt-area" value={practiceArea} onChange={(e) => setPracticeArea(e.target.value)} options={AREA_OPTIONS} />
        </Field>
        <FormError message={error} />
      </form>
    </DialogContent>
  );
}
